import type React from 'react';
import type { TFunction } from 'i18next';
import type { CompanyBudgetWorkspaceDto } from '../companyBudget/companyBudgetApi';
import type { IncomeStatementResponse } from '../incomeStatement/incomeStatementApi';
import type { DashboardMonth, DashboardScenario, DashboardTotals } from './dashboardModel';
import type { LiquidityReport } from './liquidityApi';
import type { DashboardOrder } from './ordersModel';

/** Everything a dashboard view renders from; built once by ManagementDashboard. */
export interface DashboardViewContext {
  t: TFunction;
  language: string;
  year: number;
  scenario: DashboardScenario;
  /** All 12 months of the year (charts are always the whole year). */
  months: DashboardMonth[];
  /** Months of the selected period (KPIs and tables). */
  periodMonths: DashboardMonth[];
  totals: DashboardTotals;
  monthLabels: string[];
  periodLabel: string;
  /** The period is the whole year (outside-the-sums and year-only facts apply). */
  wholeYear: boolean;
  statement: IncomeStatementResponse;
  budget: CompanyBudgetWorkspaceDto | null;
  budgetFailed: boolean;
  liquidity: LiquidityReport | null;
  liquidityFailed: boolean;
  orders: DashboardOrder[] | null;
  quotes: DashboardOrder[] | null;
  ordersFailed: boolean;
  canEditTargets: boolean;
  onEditTargets: () => void;
  /** Same scenario folding for another factor (scenario comparison table). */
  monthsForScenario: (scenario: DashboardScenario) => DashboardMonth[];
  openTab: (tab: string) => void;
  /** Opens one month (YYYY-MM) of the sales cut in „Sprzedaż i zamówienia”; absent where there is no cut. */
  openMonth?: (month: string) => void;
  /** A section the page puts next to the view's first card („Wymaga uwagi” in „Sprzedaż i zamówienia”). */
  aside?: React.ReactNode;
  /** Pulpit tiles open "Skąd ta liczba". */
  drillDown?: boolean;
  /** Refetch the report and the order list after a ZK change (forecast drag). */
  reloadResult: () => Promise<void>;
}

export const dk = (t: TFunction, key: string, options?: Record<string, unknown>): string => (
  t(`reports:managementDashboard.${key}`, options ?? {}) as string
);
